/**
 * Wall Renderer
 *
 * Creates Three.js geometry for walls with optional door openings.
 * Supports exterior walls, partition walls, and door cutouts.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { UnitEdge, DoorSpec, EdgeSide } from '@/lib/templates/types'
import {
  createWallMaterial,
  createPartitionMaterial,
  createDoorMaterial,
  applyShadowSettings,
} from '../utils/materials'

/**
 * Wall rendering options
 */
export interface WallRenderOptions {
  color?: string
  enableShadows?: boolean
  wallThickness?: number // feet
}

/**
 * Unit dimensions for wall positioning
 */
export interface UnitDimensions {
  width: number   // feet (X-axis)
  depth: number   // feet (Z-axis / Y in 2D)
  height: number  // feet
}

/**
 * Get wall length and position based on edge side and unit dimensions
 */
function getWallGeometry(
  side: EdgeSide,
  dimensions: UnitDimensions,
  thickness: number
): {
  length: number    // Wall length in feet
  position: [number, number, number]  // [x, y, z] in feet
  rotation: number  // Y-axis rotation in radians
} {
  const { width, depth, height } = dimensions
  const halfWidth = width / 2
  const halfDepth = depth / 2
  const halfHeight = height / 2
  const halfThickness = thickness / 2

  switch (side) {
    case 'back':
      // Back wall - along X-axis at -Z
      return {
        length: width,
        position: [0, halfHeight, -halfDepth - halfThickness],
        rotation: 0,
      }
    case 'front':
      // Front wall - along X-axis at +Z
      return {
        length: width,
        position: [0, halfHeight, halfDepth + halfThickness],
        rotation: 0,
      }
    case 'right':
      // Right wall - at -X to appear on viewer's right side (west position)
      // Swapped from +X to match user expectations based on camera viewing angle
      return {
        length: depth,
        position: [-halfWidth - halfThickness, halfHeight, 0],
        rotation: Math.PI / 2,
      }
    case 'left':
      // Left wall - at +X to appear on viewer's left side (east position)
      // Swapped from -X to match user expectations based on camera viewing angle
      return {
        length: depth,
        position: [halfWidth + halfThickness, halfHeight, 0],
        rotation: Math.PI / 2,
      }
  }
}

/**
 * Create a solid wall segment (no openings)
 */
function createSolidWall(
  length: number,
  height: number,
  thickness: number,
  material: THREE.Material,
  enableShadows: boolean
): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(
    length * FEET_TO_METERS,
    height * FEET_TO_METERS,
    thickness * FEET_TO_METERS
  )
  const mesh = new THREE.Mesh(geometry, material)
  applyShadowSettings(mesh, enableShadows, enableShadows)
  return mesh
}

/**
 * Create wall segments around door openings using CSG subtraction approach
 * For simplicity, we create multiple box segments to represent the wall with holes
 */
function createWallWithOpenings(
  wallLength: number,
  wallHeight: number,
  thickness: number,
  openings: DoorSpec[],
  material: THREE.Material,
  enableShadows: boolean
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'wallWithOpenings'

  // Sort openings by offset for left-to-right processing
  const sortedOpenings = [...openings].sort((a, b) => a.offset_ft - b.offset_ft)

  // Track current position along wall
  let currentPos = 0

  for (const opening of sortedOpenings) {
    // offset_ft is the leading edge (left side) of the door, not center
    const openingStart = opening.offset_ft
    const openingEnd = opening.offset_ft + opening.width_ft
    // Door center for positioning lintel and door mesh
    const doorCenterX = opening.offset_ft + opening.width_ft / 2

    // Create wall segment before this opening (if any)
    if (currentPos < openingStart) {
      const segmentLength = openingStart - currentPos
      const segmentCenterX = currentPos + segmentLength / 2 - wallLength / 2

      const segment = createSolidWall(
        segmentLength,
        wallHeight,
        thickness,
        material,
        enableShadows
      )
      segment.position.x = segmentCenterX * FEET_TO_METERS
      segment.name = 'wallSegment'
      group.add(segment)
    }

    // Create wall segment above the door (lintel)
    if (opening.height_ft < wallHeight) {
      const lintelHeight = wallHeight - opening.height_ft
      const lintelCenterX = doorCenterX - wallLength / 2
      const lintelCenterY = (wallHeight + opening.height_ft) / 2

      const lintel = new THREE.Mesh(
        new THREE.BoxGeometry(
          opening.width_ft * FEET_TO_METERS,
          lintelHeight * FEET_TO_METERS,
          thickness * FEET_TO_METERS
        ),
        material
      )
      lintel.position.set(
        lintelCenterX * FEET_TO_METERS,
        (lintelCenterY - wallHeight / 2) * FEET_TO_METERS,
        0
      )
      lintel.name = 'lintel'
      applyShadowSettings(lintel, enableShadows, enableShadows)
      group.add(lintel)
    }

    // Create door visualization (semi-transparent)
    const doorMaterial = createDoorMaterial(opening.type)
    const doorMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(
        opening.width_ft * FEET_TO_METERS,
        opening.height_ft * FEET_TO_METERS
      ),
      doorMaterial
    )
    doorMesh.position.set(
      (doorCenterX - wallLength / 2) * FEET_TO_METERS,
      (opening.height_ft / 2 - wallHeight / 2) * FEET_TO_METERS,
      thickness / 2 * FEET_TO_METERS + 0.01
    )
    doorMesh.name = `door-${opening.type}`
    group.add(doorMesh)

    currentPos = openingEnd
  }

  // Create wall segment after last opening (if any)
  if (currentPos < wallLength) {
    const segmentLength = wallLength - currentPos
    const segmentCenterX = currentPos + segmentLength / 2 - wallLength / 2

    const segment = createSolidWall(
      segmentLength,
      wallHeight,
      thickness,
      material,
      enableShadows
    )
    segment.position.x = segmentCenterX * FEET_TO_METERS
    segment.name = 'wallSegment'
    group.add(segment)
  }

  return group
}

/**
 * Render a wall from a unit edge
 */
export function renderWall(
  edge: UnitEdge,
  dimensions: UnitDimensions,
  options: WallRenderOptions = {}
): THREE.Object3D {
  const {
    color,
    enableShadows = true,
    wallThickness = 0.5,
  } = options

  // Get wall geometry based on edge side
  const { length, position, rotation } = getWallGeometry(
    edge.side,
    dimensions,
    wallThickness
  )

  // Choose material based on edge type
  const material = edge.type === 'partition'
    ? createPartitionMaterial({ color })
    : createWallMaterial({ color })

  // Create wall with or without openings
  let wall: THREE.Object3D

  if (edge.openings && edge.openings.length > 0) {
    wall = createWallWithOpenings(
      length,
      dimensions.height,
      wallThickness,
      edge.openings,
      material,
      enableShadows
    )
  } else {
    wall = createSolidWall(length, dimensions.height, wallThickness, material, enableShadows)
  }

  // Apply position (already in feet, convert to meters)
  wall.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  // Apply rotation for side walls
  if (rotation !== 0) {
    wall.rotation.y = rotation
  }

  wall.name = `wall-${edge.side}`

  return wall
}

/**
 * Render all walls for a unit based on its edges
 */
export function renderUnitWalls(
  edges: UnitEdge[],
  dimensions: UnitDimensions,
  options: WallRenderOptions = {}
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'unitWalls'

  for (const edge of edges) {
    const wall = renderWall(edge, dimensions, options)
    group.add(wall)
  }

  return group
}
